import { useI18n } from 'vue-i18n';

export interface AssetCardFacts {
  completed?: boolean;
  sponsor?: string | null;
  coinRush?: boolean;
  coins?: number | null;
  progress?: { completed: number; total: number } | null;
  duration?: number | null;
  wage?: string;
  saved?: boolean;
}

const BASE = 'unified.common.assetCard';

/**
 * Names a card with every fact its face shows. Each fact is its own self-contained phrase (so a
 * translator never assembles grammar across them), joined into one locale-aware list and appended
 * to the card's action name through `withDetails`. Each card passes only the facts it draws.
 */
export const useAssetCardName = () => {
  const { t, locale } = useI18n();

  return (name: string, facts: AssetCardFacts = {}): string => {
    const { completed, sponsor, coinRush, coins, progress, duration, wage, saved } = facts;

    const details = [
      completed ? t(`${BASE}.completed`) : null,
      sponsor ? t(`${BASE}.sponsor`, { sponsor }) : null,
      coins && coinRush ? t(`${BASE}.coinRush`) : null,
      coins ? t(`${BASE}.coins`, { count: coins }, coins) : null,
      progress ? t(`${BASE}.progress`, { completed: progress.completed, total: progress.total }) : null,
      duration ? t(`${BASE}.duration`, { count: duration }, duration) : null,
      wage ? t(`${BASE}.wage`, { wage }) : null,
      saved ? t(`${BASE}.saved`) : null,
    ].filter((detail): detail is string => !!detail);

    if (!details.length) {
      return name;
    }

    const list = new Intl.ListFormat(locale.value, { type: 'unit', style: 'short' }).format(details);

    return t(`${BASE}.withDetails`, { name, details: list });
  };
};
